import { Button, Checkbox, Container, Stack, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ClockPicker, ClockPickerView } from "@mui/x-date-pickers/ClockPicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { MouseEvent, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WeekDaysToggleButtonGroup from "../Layout/Buttons/WeekDaysToogleButtonGroup";
import { WeekDays } from "./WeekDays";

export interface StaticWeekDaysTimePickerProps {
  timeValue: Dayjs;
  weekDays: Array<WeekDays>;
  setWeekDays: (newWeekDays: Array<WeekDays>) => void;
  setTimeValue: (newTimeValue: Dayjs) => void;
  onRequestClose: (event: object, reason: string) => void;
}

export default function StaticWeekDaysTimePicker({
  timeValue,
  setTimeValue,
  weekDays,
  setWeekDays,
  onRequestClose,
}: StaticWeekDaysTimePickerProps) {
  const theme = useTheme();
  const [currentTimeValue, setCurrentTimeValue] = useState<Dayjs>(timeValue);
  const [currentWeekDays, setCurrentWeekDays] = useState<Array<WeekDays>>(weekDays);
  const [showClockPicker, setShowClockPicker] = useState<boolean>(false);

  const handleSelectionChange = (event: MouseEvent<HTMLElement>, newSelection: WeekDays[]) => {
    if (newSelection !== null) {
      setCurrentWeekDays(newSelection);
    }
  };
  const [clockView, setClockView] = useState<ClockPickerView>("hours");

  const focusHoursPicker = () => {
    setClockView("hours");
  };

  const focusMinutesPicker = () => {
    setClockView("minutes");
  };

  const handleClockValueChange = (newValue: Dayjs | null) => {
    if (newValue !== null) {
      setCurrentTimeValue(newValue);
    }
    setClockView(clockView === "hours" ? "minutes" : "hours");
  };

  const changeVisibiltyOfClockPicker = () => {
    setClockView("hours");
    setShowClockPicker(!showClockPicker);
  };

  const handleClockViewChange = (view: ClockPickerView) => {
    if (clockView === "minutes" && view === "seconds") {
      setClockView("hours");
      setShowClockPicker(false);
      return;
    }
    setClockView(view);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container sx={{ padding: 4 }}>
        <Grid container rowSpacing={1} direction="column" display="flex" justifyContent="center">
          <Grid xs display="flex" justifyContent="flex-start">
            <Typography variant="subtitle1">Opening Time</Typography>
          </Grid>
          <Grid container direction="row" display="flex" justifyContent="center">
            <Grid>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Typography variant="h3" onClick={focusHoursPicker}>
                  {currentTimeValue?.format("HH")}
                </Typography>
                <Typography variant="h3">:</Typography>
                <Typography variant="h3" onClick={focusMinutesPicker}>
                  {currentTimeValue?.format("mm")}
                </Typography>
              </Stack>
            </Grid>
            <Grid>
              <Checkbox
                checked={showClockPicker}
                onChange={changeVisibiltyOfClockPicker}
                sx={{ marginTop: 0.5, marginLeft: 2 }}
                icon={<EditIcon />}
                checkedIcon={<AccessTimeIcon />}
              />
            </Grid>
          </Grid>

          {showClockPicker && (
            <Grid xs display="flex" justifyContent="center" alignItems="center">
              <ClockPicker
                date={currentTimeValue}
                view={clockView}
                views={["hours", "minutes", "seconds"]}
                openTo="hours"
                onChange={handleClockValueChange}
                onViewChange={handleClockViewChange}
              />
            </Grid>
          )}
          <Grid
            xs
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{ paddingTop: "24px" }}
          >
            <WeekDaysToggleButtonGroup
              selection={currentWeekDays}
              onChange={handleSelectionChange}
            />
          </Grid>
          <Grid
            xs
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ paddingTop: "24px", paddingRight: "32px" }}
          >
            <Stack direction="row" spacing={2}>
              <Button
                sx={{ color: `${theme.palette.text.primary}` }}
                onClick={(e) => onRequestClose(e, "cancel")}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  setTimeValue(currentTimeValue);
                  setWeekDays(currentWeekDays);
                  onRequestClose(e, "apply");
                }}
              >
                Okay
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </LocalizationProvider>
  );
}
